import { Button } from "@loot-os/ui/components/button";
import { Command, CommandEmpty, CommandInput, CommandList } from "@loot-os/ui/components/command";
import { Popover, PopoverContent, PopoverTrigger } from "@loot-os/ui/components/popover";
import { ChevronsUpDown } from "lucide-react";
import type { ReactNode } from "react";

/**
 * The search-and-pick popover both drop-table pickers render. It owns no data: each picker
 * holds its own query state and its own procedure, and passes the matching rows as children.
 *
 * cmdk's own substring matching is off, because it would hide rows the server deliberately
 * returned.
 */
export function PickerCombobox({
  open,
  onOpenChange,
  query,
  onQueryChange,
  triggerLabel,
  searchPlaceholder,
  emptyMessage,
  disabled,
  filters,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  query: string;
  onQueryChange: (query: string) => void;
  /** What the closed trigger reads: the current pick, or an invitation to make one. */
  triggerLabel: string;
  searchPlaceholder: string;
  /** Shown while the list is empty — the search state, or why nothing came back. */
  emptyMessage: string;
  disabled?: boolean;
  /** Controls under the search box that narrow or order the rows (the catalog picker's). */
  filters?: ReactNode;
  /** The result rows, as `<CommandItem>`s. */
  children: ReactNode;
}) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            // Capped so a long item name ellipsizes instead of widening the table's column;
            // the full name is the title.
            title={triggerLabel}
            className="h-8 w-full max-w-64 min-w-0 justify-between font-normal"
          />
        }
      >
        <span className="min-w-0 truncate">{triggerLabel}</span>
        <ChevronsUpDown className="size-3.5 shrink-0 opacity-60" />
      </PopoverTrigger>
      <PopoverContent className={filters ? "w-[28rem] p-0" : "w-[22rem] p-0"} align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={query}
            onValueChange={onQueryChange}
          />
          {filters && <div className="flex gap-2 border-b p-2">{filters}</div>}
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            {children}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
